/* Top-board banner picker for airport and airline detail pages.
   Maps an entity (airport/airline) to one of the 16 curated banner illustrations
   at /images/airport-banners/*.webp and /images/airline-banners/*.webp. */

const AIRPORT_BANNERS = [
  "city", "plains", "tropical", "alpine", "lake", "mountain-village", "mediterranean", "heritage",
  "desert", "nordic", "monsoon", "clear",
];
export const AIRPORT_BANNER_KEYS = AIRPORT_BANNERS;
export function airportBannerUrl(key) {
  if (!key) return null;
  if (key.startsWith("/")) return key;
  return AIRPORT_BANNERS.includes(key) ? `/images/airport-banners/${key}.webp` : null;
}
/* Airports that get a bespoke hero (not the shared set). */
const AIRPORT_BESPOKE = {
  DEL: "/images/india/hero.png", // India Gate + Delhi terminal at sunset — reused from home hero
};
/* Coastal Indian airports that get the monsoon board during Jun–Sep. */
const MONSOON_IATA = new Set(["COK", "TRV", "IXE", "CCJ", "GOI", "GOX", "MAA", "IXZ", "STV", "VGA", "VTZ"]);
function isIndianMonsoonSeason() {
  const m = new Date().getUTCMonth(); // 0 = Jan
  return m >= 5 && m <= 8; // Jun..Sep inclusive
}
const AIRLINE_BANNERS = [
  "global", "regional", "leisure", "premium", "tropical", "beach-city", "urban", "island",
];

/* Curated overrides — a few well-known IATA/ICAO codes we want to force. */
const AIRPORT_OVERRIDES = {
  // Tropical / coastal
  COK: "tropical", TRV: "tropical", CJB: "tropical", GOI: "tropical", GOX: "tropical",
  IXE: "tropical", CCJ: "tropical", IXZ: "tropical", MLE: "tropical", HKT: "tropical",
  DPS: "tropical", HNL: "tropical", NAN: "tropical", PPT: "tropical",
  // Big city / metro
  DEL: "city", BOM: "city", BLR: "city", MAA: "city", HYD: "city", CCU: "city",
  JFK: "city", LAX: "city", LHR: "city", CDG: "city", HND: "city", DXB: "city",
  SIN: "city", HKG: "city", ICN: "city", PVG: "city", PEK: "city", FRA: "city",
  // Alpine / high
  LEH: "alpine", KUU: "alpine", IXL: "alpine", DED: "alpine",
  INN: "alpine", ZRH: "lake", GVA: "lake",
  // Mediterranean
  ATH: "mediterranean", BCN: "mediterranean", FCO: "mediterranean", MXP: "mediterranean",
  IST: "mediterranean", NCE: "mediterranean",
  // European heritage
  PRG: "heritage", VIE: "heritage", BUD: "heritage", KRK: "heritage",
  // Lake / harbour
  YYZ: "lake", ORD: "lake",
  // Desert / Middle-East
  DXB: "desert", DWC: "desert", AUH: "desert", SHJ: "desert", RUH: "desert", JED: "desert",
  DMM: "desert", DOH: "desert", KWI: "desert", BAH: "desert", MCT: "desert", CAI: "desert",
  AMM: "desert", LAS: "desert", PHX: "desert",
  // Nordic / far-north
  OSL: "nordic", ARN: "nordic", CPH: "nordic", HEL: "nordic", KEF: "nordic",
  TOS: "nordic", RVN: "nordic", LYR: "nordic", BGO: "nordic", TRD: "nordic",
  ANC: "nordic", YEG: "nordic",
};

const AIRLINE_OVERRIDES = {
  // Global flag / long-haul
  EK: "global", QR: "global", SQ: "premium", CX: "premium", EY: "premium",
  LH: "global", AF: "global", KL: "global", BA: "global", UA: "global", AA: "global", DL: "global",
  AI: "global", "6E": "urban", UK: "global",
  // Low-cost / leisure
  FR: "leisure", U2: "leisure", W6: "leisure", G8: "leisure", "5J": "leisure", AK: "leisure",
  QZ: "leisure", D7: "leisure", NK: "leisure", F9: "leisure",
  // Regional / turboprop
  "9I": "regional", "S5": "regional", "2I": "regional",
  // Island
  HA: "island", VT: "island",
  // Tropical-region majors
  MH: "tropical", GA: "tropical", SV: "beach-city",
};

/* Very light heuristic used when we don't have a curated override. */
function heuristicForAirport(a = {}) {
  const type = String(a.type || "").toLowerCase();
  const country = String(a.country || a.iso_country || "").toUpperCase();
  const name = `${a.name || ""} ${a.city || a.municipality || ""} ${a.state || a.iso_region || ""}`.toLowerCase();
  const lat = Number(a.latitude ?? a.lat ?? 0);
  const elev = Number(a.elevation_ft ?? 0);

  // Desert — Middle East + Sahara belt
  if (["AE", "SA", "QA", "KW", "BH", "OM", "JO", "IQ", "EG", "LY", "DZ", "TN", "MA"].includes(country)) return "desert";
  if (/\b(dubai|abu dhabi|riyadh|jeddah|doha|kuwait|muscat|cairo|sahara)\b/.test(name)) return "desert";
  // Nordic / far-north
  if (["IS", "NO", "SE", "FI"].includes(country) || lat > 60) return "nordic";
  if (/\b(reykjavik|tromso|rovaniemi|oslo|stockholm|helsinki|longyearbyen)\b/.test(name)) return "nordic";

  if (elev > 5000 || /\b(leh|shimla|kullu|dehradun|manali|aspen|zermatt)\b/.test(name)) return "alpine";
  if (/\b(kochi|goa|trivandrum|calicut|mangalore|port blair|male|phuket|bali|maldives|fiji)\b/.test(name)) return "tropical";
  if (/\b(geneva|zurich|como|chicago|toronto|constance)\b/.test(name)) return "lake";
  if (/\b(prague|vienna|budapest|krakow|salzburg)\b/.test(name)) return "heritage";
  if (["GR", "IT", "ES", "TR", "HR", "PT", "CY"].includes(country)) return "mediterranean";
  if (["CH", "AT"].includes(country) || /\b(alpine|mountain)\b/.test(name)) return "mountain-village";
  if (type === "international" || /\b(intl|international)\b/.test(name)) return "city";
  if (Math.abs(lat) < 25) return "tropical";
  return "plains";
}

function heuristicForAirline(a = {}) {
  const name = String(a.name || "").toLowerCase();
  const country = String(a.country || a.iso_country || "").toUpperCase();
  if (/\b(air india|emirates|british|delta|united|american|lufthansa|klm|air france)\b/.test(name)) return "global";
  if (/\b(cathay|qatar|singapore|etihad|ana|jal|swiss|thai)\b/.test(name)) return "premium";
  if (/\b(ryanair|easyjet|indigo|spicejet|air asia|jetstar|scoot|frontier|spirit|wizz|vueling)\b/.test(name)) return "leisure";
  if (/\b(alliance|feeder|regional|express|shuttle|connect|dash)\b/.test(name)) return "regional";
  if (/\b(hawaiian|air tahiti|fiji|caribbean|air seychelles|maldivian)\b/.test(name)) return "island";
  if (["IN", "PH", "TH", "ID", "MY", "LK"].includes(country)) return "tropical";
  return "urban";
}

/* Remote overrides fetched once on app boot from /api/v1/airport-banners. */
let REMOTE_OVERRIDES = {};
export function primeAirportBannerOverrides(items = []) {
  REMOTE_OVERRIDES = {};
  for (const it of items) {
    if (it?.iata && it?.key) REMOTE_OVERRIDES[String(it.iata).toUpperCase()] = it.key;
  }
}
export function getAirportBannerOverride(iata) {
  return REMOTE_OVERRIDES[String(iata || "").toUpperCase()] || null;
}

export function pickAirportBanner(airport = {}) {
  // 1. Admin/editor manual override on the record — highest priority.
  const manual = airport.banner_override;
  if (manual) {
    if (manual.startsWith("/")) return manual;
    if (AIRPORT_BANNERS.includes(manual)) return `/images/airport-banners/${manual}.webp`;
  }
  const iata = (airport.iata || airport.iata_code || airport.code || "").toUpperCase();
  // 2. Remote override loaded from the admin table.
  const remote = REMOTE_OVERRIDES[iata];
  if (remote) {
    if (remote.startsWith("/")) return remote;
    if (AIRPORT_BANNERS.includes(remote)) return `/images/airport-banners/${remote}.webp`;
  }
  // 3. Airports with their own bespoke hero (e.g. DEL → India Gate).
  if (AIRPORT_BESPOKE[iata]) return AIRPORT_BESPOKE[iata];
  // 4. Seasonal switch — Indian coastal airports get the monsoon board Jun–Sep.
  if (MONSOON_IATA.has(iata) && isIndianMonsoonSeason()) return "/images/airport-banners/monsoon.webp";
  // 5. Curated IATA override.
  const override = AIRPORT_OVERRIDES[iata];
  const key = override || heuristicForAirport(airport);
  return `/images/airport-banners/${AIRPORT_BANNERS.includes(key) ? key : "city"}.webp`;
}

export function pickAirlineBanner(airline = {}) {
  const iata = String(airline.iata || airline.iata_code || airline.code || "").toUpperCase();
  const override = AIRLINE_OVERRIDES[iata];
  const key = override || heuristicForAirline(airline);
  return `/images/airline-banners/${AIRLINE_BANNERS.includes(key) ? key : "global"}.webp`;
}
